import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Plus, 
  MoreVertical, 
  Edit, 
  Share2, 
  Copy, 
  Trash2, 
  Calendar, 
  Tag, 
  User, 
  FileText, 
  Settings, 
  Star, 
  MessageSquare, 
  Link, 
  CheckCircle 
} from 'lucide-react';
import { Task } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import '../index.css';
import SideModal, { BreadcrumbItem } from '../components/SideModal';
import TaskDetail from '../components/TaskDetail';

const Tasks: React.FC = () => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [taskNotes, setTaskNotes] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: ''
  });

  useEffect(() => {
    fetchTasks();
    
    // Check if we should open create modal from URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('create') === 'true') {
      setShowCreateModal(true);
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && !(event.target as Element).closest('.dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const fetchTasks = async () => {
    try {
      const response = await api.get('/api/tasks');
      setTasks(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setLoading(false);
    }
  };

  const createTask = async () => {
    try {
      await api.post('/api/tasks', newTask);
      setNewTask({ title: '', description: '', priority: 'medium', due_date: '' });
      setShowCreateModal(false);
      fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const updateTask = async (taskId: string, updates: any) => {
    try {
      await api.put(`/api/tasks/${taskId}`, updates);
      fetchTasks();
      setShowEditModal(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task. Please try again.');
    }
  };

  const moveTask = async (taskId: string, newStatus: string) => {
    try {
      await api.patch(`/api/tasks/${taskId}/move`, { status: newStatus });
      fetchTasks();
    } catch (error) {
      console.error('Error moving task:', error);
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      await api.delete(`/api/tasks/${taskId}`);
      fetchTasks();
      setOpenDropdown(null);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const shareTask = async (task: Task) => {
    // Generate shareable link
    const shareUrl = `${window.location.origin}/tasks/${task.id}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Task link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
    setOpenDropdown(null);
  };

  const duplicateTask = async (task: Task) => {
    try {
      const duplicatedTask = {
        title: `${task.title} (Copy)`,
        description: task.description,
        priority: task.priority,
        due_date: task.due_date,
        project_name: task.project_name,
        assigned_user_name: task.assigned_user_name
      };
      await api.post('/api/tasks', duplicatedTask);
      fetchTasks();
    } catch (error) {
      console.error('Error duplicating task:', error);
    }
    setOpenDropdown(null);
  };

  const editTask = (task: Task) => {
    setEditingTask(task);
    setShowEditModal(true);
    setOpenDropdown(null);
  };

  const toggleDropdown = (taskId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenDropdown(openDropdown === taskId ? null : taskId);
  };

  const getTasksByStatus = (status: string) => {
    return tasks.filter(task => task.status === status);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      case 'todo': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const openTaskDetail = (task: Task) => {
    setSelectedTask(task);
    setTaskNotes(task.description || '');
    setShowTaskDetail(true);
  };

  const saveTaskNotes = async () => {
    if (!selectedTask) return;
    
    try {
      // Use PATCH for partial update of just the description
      await api.patch(`/api/tasks/${selectedTask.id}`, { description: taskNotes });
      setShowTaskDetail(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Error saving task notes:', error);
      alert('Failed to save task notes. Please try again.');
    }
  };

  const columns = [
    { id: 'todo', title: 'To Do', color: 'bg-gray-50' },
    { id: 'in-progress', title: 'In Progress', color: 'bg-yellow-50' },
    { id: 'completed', title: 'Completed', color: 'bg-green-50' }
  ];

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${colors.background}`}>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="h-8 w-40 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, colIdx) => (
              <div key={colIdx} className="rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50 bg-white/60 dark:bg-slate-800/60">
                <div className="flex justify-between items-center mb-4">
                  <div className="h-5 w-24 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse" />
                  <div className="h-5 w-10 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse" />
                </div>
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="p-4 rounded-xl border border-slate-200/50 dark:border-slate-700/50 bg-white/70 dark:bg-slate-800/70">
                      <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse mb-2" />
                      <div className="h-3 w-full bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse mb-1" />
                      <div className="h-3 w-2/3 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.background}`}>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="text-gray-600">Manage and organize your tasks across projects.</p>
        </div>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Task
        </button>
      </div>

      {/* Task Board */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {columns.map((column) => (
          <div key={column.id} className={`${column.color} rounded-xl p-4 border border-gray-200`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-gray-900">{column.title}</h3>
              <span className="badge bg-white text-gray-600 border border-gray-200">
                {getTasksByStatus(column.id).length}
              </span>
            </div>
            
            <div className="space-y-3">
              {getTasksByStatus(column.id).map((task) => (
                <div 
                  key={task.id} 
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  onClick={() => openTaskDetail(task)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                      {task.title}
                    </h4>
                    <div className="relative dropdown-container">
                      <button 
                        className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
                        onClick={(e) => toggleDropdown(task.id, e)}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {/* Dropdown Menu */}
                      {openDropdown === task.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-200 z-10">
                          <div className="py-2">
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                editTask(task);
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Edit className="w-4 h-4 mr-3" />
                              Edit Task
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                shareTask(task);
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Share2 className="w-4 h-4 mr-3" />
                              Share Task
                            </button>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                duplicateTask(task);
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              <Copy className="w-4 h-4 mr-3" />
                              Duplicate Task
                            </button>
                            <hr className="my-2 border-gray-200" />
                            <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteTask(task.id);
                              }}
                              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="w-4 h-4 mr-3" />
                              Delete Task
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {task.description && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{task.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center space-x-3">
                      {task.project_name && (
                        <span className="flex items-center">
                          <Tag className="w-3 h-3 mr-1" />
                          {task.project_name}
                        </span>
                      )}
                      {task.assigned_user_name && (
                        <span className="flex items-center">
                          <User className="w-3 h-3 mr-1" />
                          {task.assigned_user_name}
                        </span>
                      )}
                    </div>
                    {task.due_date && (
                      <span className="flex items-center">
                        <Calendar className="w-3 h-3 mr-1" />
                        {new Date(task.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex justify-between mt-3 pt-3 border-t border-gray-200">
                    <div className="flex space-x-1">
                      {column.id !== 'completed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const nextStatus = column.id === 'todo' ? 'in-progress' : 'completed';
                            moveTask(task.id, nextStatus);
                          }}
                          className="text-xs btn-secondary"
                        >
                          {column.id === 'todo' ? 'Start' : 'Complete'}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-8 w-full max-w-md shadow-2xl border border-gray-200">
            <h3 className="text-xl font-semibold mb-6">Create New Task</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Task Title</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter task title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Enter task description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                <input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createTask}
                disabled={!newTask.title.trim()}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditModal && editingTask && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-8 w-full max-w-md shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-6">Edit Task</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Task Title</label>
                <input
                  type="text"
                  value={editingTask.title}
                  onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter task title"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={editingTask.description}
                  onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Enter task description"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <select
                  value={editingTask.priority}
                  onChange={(e) => setEditingTask({...editingTask, priority: e.target.value as 'low' | 'medium' | 'high'})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={editingTask.status}
                  onChange={(e) => setEditingTask({...editingTask, status: e.target.value as 'todo' | 'in-progress' | 'completed'})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="todo">To Do</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                <input
                  type="date"
                  value={editingTask.due_date ? editingTask.due_date.split('T')[0] : ''}
                  onChange={(e) => setEditingTask({...editingTask, due_date: e.target.value})}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTask(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => updateTask(editingTask.id, editingTask)}
                className="btn-primary"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Side Drawer */}
      <SideModal
        isOpen={showTaskDetail && !!selectedTask}
        onClose={() => setShowTaskDetail(false)}
        title={selectedTask ? selectedTask.title : 'Task'}
        breadcrumbs={((): BreadcrumbItem[] => {
          if (!selectedTask) return [];
          const crumbs: BreadcrumbItem[] = [
            { id: 'home', title: 'Home', type: 'home' },
          ];
          if (selectedTask.project_name) {
            crumbs.push({ id: 'project', title: selectedTask.project_name, type: 'project' });
          }
          crumbs.push({ id: selectedTask.id, title: selectedTask.title, type: 'task' });
          return crumbs;
        })()}
        size="xl"
      >
        {selectedTask && (
          <TaskDetail
            task={selectedTask}
            subTasks={tasks.filter(t => t.parent_task_id === selectedTask.id)}
            onEdit={(task) => setEditingTask(task)}
            onDelete={(taskId) => { setShowTaskDetail(false); deleteTask(taskId); }}
            onSubTaskClick={(task) => openTaskDetail(task)}
            onAddSubTask={async (parentTaskId) => {
              try {
                await api.post('/api/tasks', { title: 'New Task', description: '', priority: 'medium', status: 'todo', parent_task_id: parentTaskId });
                fetchTasks();
              } catch (e) { console.error(e); }
            }}
            onStatusChange={(taskId, status) => moveTask(taskId, status)}
            onPriorityChange={(taskId, priority) => updateTask(taskId, { priority })}
            onAssignUser={() => {}}
          />
        )}
      </SideModal>
        </div>
      </div>
    </div>
  );
};

export default Tasks; 